import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import CountUp from 'react-countup';
import axios from 'axios';
import {
  TrendingUp,
  Video,
  Palette,
  Target,
  MapPin as MapPinIcon,
  Users,
  FileText,
  Eye,
  Star,
  ArrowRight,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const [visitorCount, setVisitorCount] = useState(127);

  useEffect(() => {
    // Fetch and increment visitor count
    const trackVisitor = async () => {
      try {
        const response = await axios.post(`${API}/visitors/increment`);
        setVisitorCount(response.data.count);
      } catch (error) {
        console.error('Error tracking visitor:', error);
      }
    };
    trackVisitor();
  }, []);

  const services = [
    {
      icon: <TrendingUp size={32} />,
      title: 'Social Media Management',
      description: 'Instagram & Facebook management, content strategy, and audience growth',
    },
    {
      icon: <Video size={32} />,
      title: 'Video Editing',
      description: 'Professional reels editing, promotional videos, and high-engagement content',
    },
    {
      icon: <Palette size={32} />,
      title: 'Creative Design',
      description: 'Social media posts, ad creatives, and brand visuals that convert',
    },
    {
      icon: <Target size={32} />,
      title: 'Meta Ads Management',
      description: 'Facebook & Instagram ads with conversion optimization',
    },
    {
      icon: <MapPinIcon size={32} />,
      title: 'GMB Optimization',
      description: 'Local SEO, business ranking, and profile management',
    },
  ];

  const stats = [
    { number: 500, label: 'Clients Served', suffix: '+' },
    { number: 10, label: 'Social Media Posts', suffix: 'K+' },
    { number: 5, label: 'Ad Reach Generated', suffix: 'M+' },
  ];

  const testimonials = [
    {
      name: 'Rahul Sharma',
      business: 'Travel Agency',
      rating: 5,
      text: 'Optive Digital transformed our social media presence. Our bookings increased by 300% in 3 months!',
    },
    {
      name: 'Priya Patel',
      business: 'Restaurant Owner',
      rating: 5,
      text: 'The creative designs and reels they create are absolutely stunning. Our engagement has skyrocketed!',
    },
    {
      name: 'Amit Kumar',
      business: 'Real Estate',
      rating: 5,
      text: 'Professional team with amazing results. Their Meta ads campaign generated quality leads consistently.',
    },
  ];

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
        {/* Background */}
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/50 to-black z-10"></div>
          <img
            src="https://static.prod-images.emergentagent.com/jobs/2304d325-e856-415f-87c3-0487b81fdef4/images/8d95c52a8a91e5803410c345647bccb3f3777a3ff0073143c6081eda7d527af3.png"
            alt="Hero Background"
            className="w-full h-full object-cover opacity-40"
          />
        </div>

        {/* Content */}
        <div className="relative z-20 max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl md:text-7xl font-bold tracking-tight leading-tight mb-6" data-testid="hero-headline">
              <span className="gradient-text">Scale Your Brand With</span>
              <br />
              <span className="gold-gradient-text">Premium Digital Marketing</span>
            </h1>
            <p className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto mb-10 leading-relaxed" data-testid="hero-subheadline">
              We help businesses grow with powerful social media marketing, creative content, and high-converting advertising.
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
              <Link
                to="/packages"
                data-testid="hero-cta-packages"
                className="bg-gradient-to-r from-[#3B82F6] to-[#2563EB] text-white px-8 py-4 rounded-full font-semibold hover:shadow-[0_0_30px_rgba(59,130,246,0.6)] transition-all hover:scale-105"
              >
                View Packages
              </Link>
              <Link
                to="/contact"
                data-testid="hero-cta-consultation"
                className="bg-white/5 border border-white/10 hover:bg-gradient-to-b from-[#0A0A0A] to-black/10 text-white px-8 py-4 rounded-full font-semibold backdrop-blur-md transition-all"
              >
                Get Free Consultation
              </Link>
            </div>

            {/* Live Visitor Counter */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1, duration: 0.6 }}
              className="inline-flex items-center space-x-2 glass px-6 py-3 rounded-full"
              data-testid="visitor-counter"
            >
              <Eye size={20} className="text-[#3B82F6]" />
              <span className="text-sm text-gray-300">
                <span className="text-white font-semibold">{visitorCount}+</span> people are currently viewing this website
              </span>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-24 bg-gradient-to-b from-black to-[#0A0A0A]" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center glass rounded-2xl p-8 hover:border-white/20 transition-colors"
                data-testid={`stat-${index}`}
              >
                <div className="text-5xl md:text-6xl font-bold mb-2 gold-gradient-text">
                  <CountUp end={stat.number} duration={2.5} />
                  {stat.suffix}
                </div>
                <div className="text-gray-400">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-32 relative" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4 gradient-text">Our Services</h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Comprehensive digital marketing solutions to elevate your brand
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -5 }}
                className="glass rounded-2xl p-8 hover:border-white/20 transition-all group"
                data-testid={`service-card-${index}`}
              >
                <div className="text-[#3B82F6] mb-4 group-hover:scale-110 transition-transform">
                  {service.icon}
                </div>
                <h3 className="text-xl font-semibold mb-3 text-white">{service.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{service.description}</p>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/services"
              data-testid="view-all-services-btn"
              className="inline-flex items-center space-x-2 text-[#3B82F6] hover:text-[#2563EB] font-semibold transition-colors"
            >
              <span>View All Services</span>
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-32 bg-gradient-to-b from-[#0A0A0A] to-black" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4 gradient-text">Client Success Stories</h2>
            <p className="text-gray-400 text-lg">What our clients say about us</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="glass rounded-2xl p-8"
                data-testid={`testimonial-${index}`}
              >
                <div className="flex mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} size={16} className="text-[#FFD700] fill-[#FFD700]" />
                  ))}
                </div>
                <p className="text-gray-300 mb-6 leading-relaxed">"{testimonial.text}"</p>
                <div>
                  <div className="font-semibold text-white">{testimonial.name}</div>
                  <div className="text-sm text-gray-400">{testimonial.business}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 relative overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0 bg-gradient-to-tr from-blue-600/20 via-purple-500/10 to-transparent"></div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6 gradient-text">
              Ready to Scale Your Brand?
            </h2>
            <p className="text-gray-300 text-lg mb-10 max-w-2xl mx-auto">
              Let's create a winning digital marketing strategy for your business
            </p>
            <Link
              to="/contact"
              data-testid="final-cta-button"
              className="bg-gradient-to-r from-[#FFD700] to-[#FDB931] text-black font-bold px-10 py-4 rounded-full hover:shadow-[0_0_30px_rgba(255,215,0,0.5)] transition-all hover:-translate-y-1 inline-block"
            >
              Get Free Consultation
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
